import React, { useEffect, useLayoutEffect, useState, useMemo, useRef } from "react";
import { setNavJoin } from "../utils/navJoin";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import "../index.css";
import "./CommunityHome.css";
import { useAuthContext } from "../hooks/useAuthContext";
import { getAuth } from "firebase/auth";
import Cookies from "js-cookie";
import StepsExplainer from "../components/StepsExplainer";
import StepsExplainer_CommunityHome from "../components/StepsExplainer_CommunityHome";
import StepsExplainer_CommunityHomeSection2 from "../components/StepsExplainer_CommunityHomeSection2";
import DemoCommunityBot from "../components/DemoCommunityBot/DemoCommunityBot";
import WeeklyMatchCountdown from "../components/WeeklyMatchCountdown";
import { getDemoButtonVariant, demoButtonForced, logDemoButtonExposure, getHeroFlowVariant, logHeroFlowExposure, getAboutVideoVariant, logAboutVideoExposure, getJoinRouteVariant, logJoinRouteExposure } from "../utils/abTest";
import useCommunityPageAnalytics from "../hooks/useCommunityPageAnalytics";
import LazyBackground from "../components/LazyBackground";
import { sizedImageUrl, heroSrcSet, isNearSlide } from "../utils/imageSizing";
import { taglineEnding } from "../utils/homePageTagline";
import HeroStory from "../components/HeroStory";
import HeroVideo from "../components/HeroVideo";
import AboutVideo, { AboutVideoInline } from "../components/AboutVideo";
import QuickMatchHero, { C_OPENS_ON_CHOICE } from "../components/QuickMatchHero";

// Hero-flow A/B (community.heroFlowABTest): what the other half of visitors
// get against A (the normal hero). 'B' = the quick-match card, 'C' = the same
// card opening on a choice — join now, or a quick look at who's near first.
const HERO_FLOW_TEST_ARM = 'C';

// Interest strip A/B — 'v1' = tiered community/sitewide/quiet count,
// 'v2' = flat site-wide activity total. Local-only toggle for comparing
// the two while testing, not yet wired into anything real.
const INTEREST_STRIP_VERSION = 'v2';

// Countdown-mode ('interconnectingMode: countdown') strip A/B — 'vc1' is
// the hero-only treatment (title/tagline swap + countdown box), 'vc2'
// adds a second strip right below the hero spelling out exactly what the
// weekly round means (how many people, what for). Local-only toggle.
const COUNTDOWN_STRIP_VERSION = 'vc2';

// We match on quality, not headcount — a quiet community gets a warm,
// proactive message instead of a borrowed number from elsewhere on the site.
// matchLabel is per-community (defaults to "friend" — see communityModel.js)
// so the closing line fits the community, e.g. "a new golf partner is born"
// or "a new friend who loves visiting Florence as much as you is born".
const getQuietMessage = (matchLabel, openingLine = "But it's not about numbers.") => (
  <>
    <p style={{ margin: '0 0 0.75rem', color: '#c4c4c4' }}>{openingLine}</p>
    <p style={{ margin: 0, color: '#9a9a9a' }}>
      It's not an endless conveyor belt of swiping or a wall of people. It's quality over quantity. If no one's 
      quite the right fit, we'll let you know when a good match joins. 
      We only get in touch if there are people who truly seem a good fit. 
      So we can be quiet for days, weeks, months, or even years! 
      And then, boom, a new {matchLabel || 'friend'} is born :)
    </p>
  </>
);

// Slot-machine-style count-up — whizzes from 0 to `value` then settles.
// Only runs the count-up the first time the number scrolls into view —
// not on page load, since the strip usually isn't in the viewport yet.
const AnimatedCount = ({ value }) => {
  const [display, setDisplay] = useState(0);
  const spanRef = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const node = spanRef.current;
    if (!node) return;

    if (hasAnimated.current) {
      setDisplay(value);
      return;
    }

    let raf;
    const runAnimation = () => {
      const duration = 1200;
      const start = performance.now();
      const tick = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
        setDisplay(Math.round(eased * value));
        if (progress < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAnimated.current) {
        hasAnimated.current = true;
        runAnimation();
        observer.disconnect();
      }
    }, { threshold: 0.4 });
    observer.observe(node);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value]);

  return <span ref={spanRef}>{display}</span>;
};

const DEFAULT_TESTIMONIALS = [
  {
    text: 'Friendly, professional and efficient to help us get things set up. Was a really easy process that matched me well with others.',
    author: 'Community Member'
  },
  {
    text: 'A really good service and helps find people in similar circumstances and hopefully lead to friendship. It was good to meet new people and share our stories.',
    author: 'Community Member'
  }
];

// Community data inlined into the pre-built page (scripts/generate-og-pages.js)
// — only valid for the community the browser first landed on.
const getInlinedCommunity = (ref) => {
  const c = typeof window !== "undefined" ? window.__COMMUNITY__ : null;
  return c && ref && c.databaseRef && c.databaseRef.toLowerCase() === String(ref).toLowerCase() ? c : null;
};

const CommunityHome = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, dispatch, refreshUserProfile } = useAuthContext();
  const url = new URL(location.pathname, "http://dummy.com");
  const params = url.pathname.split("/").filter(Boolean).pop();
  const [community, setCommunity] = useState(() => getInlinedCommunity(params));
  const [csrfToken, setCsrfToken] = useState(null);
  const [isLoading, setIsLoading] = useState(() => !getInlinedCommunity(params));
  // Path of the static hero shell this render is taking over from, if any.
  // The shell already played the entrance animations — replaying them on the
  // real page would flash (fade from transparent) and jump (slide up).
  const [shellPath] = useState(() => document.getElementById("static-shell")?.dataset.path?.toLowerCase() || null);
  const tookOverFromShell = shellPath === `/${String(params).toLowerCase()}`;
  const [isNotFound, setIsNotFound] = useState(false);
  const [needsDarkOverlay, setNeedsDarkOverlay] = useState(null); // null = not analyzed yet
  const [needsTopGradient, setNeedsTopGradient] = useState(true); // Default to true for better navbar visibility
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [heroCarouselIndex, setHeroCarouselIndex] = useState(0);
  const [interestBadge, setInterestBadge] = useState(null); // { scope, count } | null
  // A/B test: does showing the "Demo" button change signup conversion?
  // Variant is assigned once per browser (localStorage) and reused everywhere.
  const [showDemoButton] = useState(() => getDemoButtonVariant() === 'A');

  // Scroll to top when component mounts or community changes
  useEffect(() => {
    // Use requestAnimationFrame to ensure scroll happens after browser paint/restoration
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'instant' }));
  }, [params, community]); // Trigger on both params and community change

  const PLACEHOLDER_IMG = "https://assets.streamlinehq.com/image/private/w_512,h_512,ar_1/f_auto/v1/icons/3/shooting-star-pnhn77eiumd9cca9iyw1b.png/shooting-star-ey0t57fhladsuoxiccij7.png?_a=DATAdtAAZAA0";

  // Function to analyze image brightness
  const analyzeImageBrightness = (imageUrl) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        // Analyse a 64px-wide copy — same proportions, so the same answer,
        // without decoding and looping over a 24-megapixel original.
        canvas.width = 64;
        canvas.height = Math.max(1, Math.round(64 * img.height / img.width));
        
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        let totalBrightness = 0;
        let pixelCount = 0;
        let whitePixelCount = 0;
        
        // Detect if we're on mobile/tablet vs desktop
        const isMobile = window.innerWidth <= 768;
        
        // Different analysis areas for mobile vs desktop
        let navbarWhitePixelCount = 0;
        let navbarPixelCount = 0;
        let topBoundary, leftBoundary, rightBoundary;
        
        if (isMobile) {
          // Mobile: analyze top 15% and full width (since image positioning differs)
          topBoundary = Math.floor(canvas.height * 0.15);
          leftBoundary = 0;
          rightBoundary = canvas.width;
        } else {
          // Desktop: analyze top 10% and left 2/3 (original logic)
          topBoundary = Math.floor(canvas.height * 0.1);
          leftBoundary = 0;
          rightBoundary = Math.floor(canvas.width * 0.67);
        }
        
        // Every pixel of the 64px copy (each already averages many originals)
        for (let y = 0; y < canvas.height; y += 1) {
          for (let x = 0; x < canvas.width; x += 1) {
            const i = (y * canvas.width + x) * 4;
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            
            // Calculate relative luminance
            const brightness = (0.299 * r + 0.587 * g + 0.114 * b);
            totalBrightness += brightness;
            pixelCount++;
            
            // Check if pixel is white/very light (all RGB values above 200)
            if (r > 200 && g > 200 && b > 200) {
              whitePixelCount++;
            }
            
            // Check navbar area specifically based on device type
            if (y <= topBoundary && x >= leftBoundary && x <= rightBoundary) {
              navbarPixelCount++;
              if (r > 200 && g > 200 && b > 200) { // Standard white threshold for navbar area
                navbarWhitePixelCount++;
              }
            }
          }
        }
        
        const avgBrightness = totalBrightness / pixelCount;
        const whitePercentage = (whitePixelCount / pixelCount) * 100;
        const navbarWhitePercentage = navbarPixelCount > 0 ? (navbarWhitePixelCount / navbarPixelCount) * 100 : 0;
        
        console.log('🔍 Navbar area analysis:', {
          deviceType: isMobile ? 'Mobile' : 'Desktop',
          navbarWhitePercentage: navbarWhitePercentage.toFixed(2) + '%',
          navbarPixelCount,
          navbarWhitePixelCount,
          avgBrightness: avgBrightness.toFixed(2),
          whitePercentage: whitePercentage.toFixed(2) + '%',
          topBoundary,
          leftBoundary,
          rightBoundary,
          analysisArea: `${topBoundary}px high, ${leftBoundary}-${rightBoundary}px wide`
        });
        
        // Original overlay logic
        const needsOverlay = whitePercentage >= 20 || avgBrightness > 140;
        setNeedsDarkOverlay(needsOverlay);
        
        // Top gradient logic - different thresholds for mobile vs desktop
        const threshold = isMobile ? 10 : 5; // Higher threshold for mobile since we're analyzing more area
        const needsTopGrad = navbarWhitePercentage >= threshold;
        console.log('🎯 Top gradient needed:', needsTopGrad, 'due to navbar white percentage:', navbarWhitePercentage.toFixed(2) + '% (threshold: ' + threshold + '%)');
        setNeedsTopGradient(needsTopGrad);
      } catch (error) {
        console.log('Could not analyze image brightness:', error);
        // Default to no overlay on error, keep gradient for navbar visibility
        setNeedsDarkOverlay(false);
        setNeedsTopGradient(true);
      }
    };
    
    img.onerror = () => {
      console.log('⚠️ Image analysis failed - applying default gradient for navbar visibility');
      setNeedsDarkOverlay(false);
      setNeedsTopGradient(true); // Force gradient when we can't analyze
    };
    
    // Add timeout in case image never loads
    setTimeout(() => {
      if (!img.complete) {
        console.log('⏰ Image load timeout - forcing gradient and no overlay');
        setNeedsDarkOverlay(false);
        setNeedsTopGradient(true);
      }
    }, 3000);
    
    img.src = sizedImageUrl(imageUrl, 64);
  };


  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const getCommunityDisplay = async () => {
      // With inlined data the page is already rendered — refresh quietly
      // in the background instead of flashing the loading skeleton.
      const inlined = getInlinedCommunity(params);
      if (inlined) setCommunity(inlined);
      else setIsLoading(true);
      setIsNotFound(false);
      try {
        const url = `${process.env.REACT_APP_API_BASE_URL}/communities/fulldisplay/${encodeURIComponent(params)}`;
        // Fetch without the auth token first — it's only ever needed to
        // unlock hidden/private test communities, so paying for
        // getIdToken() (and the CORS preflight an Authorization header
        // forces) up front would slow down every single page load for
        // every logged-in visitor, for a case that almost never applies.
        // Only reach for it if the community turns out not to exist,
        // since it might be a hidden one this user is allowed to see.
        let response = await fetch(url, {
          method: "GET",
          headers: { "Content-Type": "application/json" }
        });
        if (response.status === 404) {
          const currentUser = getAuth().currentUser;
          if (currentUser) {
            response = await fetch(url, {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${await currentUser.getIdToken()}`
              }
            });
          }
          if (response.status === 404) {
            setIsNotFound(true);
            return;
          }
        }
        if (!response.ok) throw new Error("Network response was not ok");
        const communityData = await response.json();
        if (typeof communityData !== "object" || communityData === null)
          throw new Error("Invalid JSON data");
        setCommunity(communityData);
        // Community was found via a previous (renamed-away-from) slug —
        // land on the canonical URL instead of silently serving old-URL content.
        if (communityData.databaseRef && communityData.databaseRef.toLowerCase() !== params.toLowerCase()) {
          navigate(`/${communityData.databaseRef}${window.location.search}${window.location.hash}`, { replace: true }); // keep ?preview= etc.
        }
      } catch (error) {
        console.error("Failed to fetch community full display:", error);
      } finally {
        setIsLoading(false);
      }
    };
    getCommunityDisplay();
    const fetchCsrfToken = async () => {
      const csrfToken = Cookies.get("mytoken");
      setCsrfToken(csrfToken);
    };
    fetchCsrfToken();
  }, [params]);

  // Force re-render when user communities change
  useEffect(() => {
    console.log('🔄 [Community Home] User communities updated:', user?.listOfCommunities);
    // This effect will cause the component to re-evaluate isUserMember()
  }, [user?.listOfCommunities]);

  const sanitizedCommunity = useMemo(() => {
    return community
      ? {
          ...community,
          title: DOMPurify.sanitize(community?.title),
          pics: (() => { const raw = community?.pics; if (!raw) return ''; return Array.isArray(raw) ? raw.join(',') : DOMPurify.sanitize(String(raw)); })(),
          topLevelSummary: DOMPurify.sanitize(community?.topLevelSummary),
          type: DOMPurify.sanitize(community?.type),
          location: DOMPurify.sanitize(community?.location),
          mainDescription: DOMPurify.sanitize(community?.mainDescription),
          tags: DOMPurify.sanitize(community?.tags),
          eligibilityDescription: DOMPurify.sanitize(community?.eligibilityDescription || ''),
          matchingOptions: DOMPurify.sanitize(community?.matchingOptions || ''),
          matchLabel: DOMPurify.sanitize(community?.matchLabel || 'friend'),
          interconnectingMode: community?.interconnectingMode === 'countdown' ? 'countdown' : 'original',
          suggestionsRounds: (community?.suggestionsRounds && typeof community.suggestionsRounds.dayOfWeek === 'number' && typeof community.suggestionsRounds.hour === 'number')
            ? { dayOfWeek: community.suggestionsRounds.dayOfWeek, hour: community.suggestionsRounds.hour }
            : null,
          heroTaglineCountdown: DOMPurify.sanitize(community?.heroTaglineCountdown || ''),
          countdownLabel: DOMPurify.sanitize(community?.countdownLabel || ''),
          connectionReasons: community?.connectionReasons?.map(reason => DOMPurify.sanitize(reason)) || [],
          costStructure: community?.costStructure ? {
            ...community.costStructure,
            description: DOMPurify.sanitize(community.costStructure.description || 'Free to join!')
          } : { isFree: true, price: '', description: 'Free to join!' },
          homePageTagline: DOMPurify.sanitize(community?.homePageTagline || 'anyone looking to connect and build meaningful relationships'),
          heroTagline: DOMPurify.sanitize(community?.heroTagline || ''),
          testimonials: (community?.testimonials || []).map(t => ({
            text: DOMPurify.sanitize(t.text || ''),
            author: DOMPurify.sanitize(t.author || '')
          })),
          carousel: community?.carousel || false,
          imageStrips: community?.imageStrips || false,
          homePageLayout: community?.homePageLayout || 'v1',
          // 'story' = heroStory is typed out over the hero photo instead of the tagline.
          heroStyle: community?.heroStyle === 'story' && community?.heroStory ? 'story' : 'default',
          heroStory: DOMPurify.sanitize(community?.heroStory || '', { ALLOWED_TAGS: [] }),
          heroStoryEffect: community?.heroStoryEffect === 'fade' ? 'fade' : 'type',
          heroVideo: /^(https:\/\/|\/(?!\/))/i.test(community?.heroVideo || '') ? community.heroVideo : '',
          heroVideoMobile: /^(https:\/\/|\/(?!\/))/i.test(community?.heroVideoMobile || '') ? community.heroVideoMobile : '',
          heroVideoPoster: /^(https:\/\/|\/(?!\/))/i.test(community?.heroVideoPoster || '') ? community.heroVideoPoster : '',
          aboutVideo: /^(https:\/\/|\/(?!\/))/i.test(community?.aboutVideo || '') ? community.aboutVideo : '',
          aboutVideoPoster: /^(https:\/\/|\/(?!\/))/i.test(community?.aboutVideoPoster || '') ? community.aboutVideoPoster : '',
          aboutVideoLabel: String(community?.aboutVideoLabel || '').slice(0, 60),
          aboutVideoABTest: community?.aboutVideoABTest === true,
          joinChatABTest: community?.joinChatABTest === true,
          heroFlow: community?.heroFlow === 'quickmatch' ? 'quickmatch' : 'default',
          heroFlowABTest: !!community?.heroFlowABTest,
          quickMatchPeopleWord: DOMPurify.sanitize(community?.quickMatchPeopleWord || '', { ALLOWED_TAGS: [] }),
          quickMatchFields: Array.isArray(community?.quickMatchFields) ? community.quickMatchFields : [],
          quickMatchRetentionMonths: Number(community?.quickMatchRetentionMonths) || 0,
          quickMatchCountry: DOMPurify.sanitize(community?.quickMatchCountry || '', { ALLOWED_TAGS: [] }),
          quickMatchStyle: community?.quickMatchStyle === 'floating' ? 'floating' : 'panel',
        }
      : null;
  }, [community]);

  // The demo simulates instant on-demand suggestions, which isn't how
  // countdown-mode (weekly batch) communities actually work — hide it
  // there rather than show a misleading demo of behavior that doesn't
  // happen for this community.
  const shouldShowDemoButton = showDemoButton && sanitizedCommunity?.interconnectingMode !== 'countdown';

  // Quick-match hero (community.heroFlow) — for visitors who aren't logged
  // in. With heroFlowABTest on, only variant B gets it (A keeps the normal
  // hero), and the exposure is logged for the 'community_hero_flow' test.
  const heroFlowCommunityId = sanitizedCommunity?.communityId || community?._id;
  const heroFlowTesting = sanitizedCommunity?.heroFlow === 'quickmatch' && sanitizedCommunity?.heroFlowABTest && !user;
  const heroFlowVariant = useMemo(
    () => (heroFlowTesting && heroFlowCommunityId ? getHeroFlowVariant(heroFlowCommunityId, HERO_FLOW_TEST_ARM) : null),
    [heroFlowTesting, heroFlowCommunityId]
  );
  // ?preview=quickmatch / ?preview=choice / ?preview=normal force one (e.g. to
  // check any while logged in, or whatever A/B group this browser is in).
  const heroPreview = new URLSearchParams(location.search).get('preview');
  const showQuickMatch = sanitizedCommunity?.heroFlow === 'quickmatch'
    && sanitizedCommunity?.quickMatchFields?.length > 0
    && heroPreview !== 'normal'
    && (heroPreview === 'quickmatch' || heroPreview === 'choice'
      || (!user && (!sanitizedCommunity?.heroFlowABTest || heroFlowVariant === 'B' || heroFlowVariant === 'C')));
  // C: the tagline dissolves out once they choose "See who's near me first".
  const [quickMatchTaglineOut, setQuickMatchTaglineOut] = useState(false);
  // Version C: the card opens on "join now, or look first" (QuickMatchHero intro).
  const quickMatchIntro = heroPreview === 'choice'
    || (heroPreview !== 'quickmatch' && (heroFlowVariant === 'C' || (!sanitizedCommunity?.heroFlowABTest && HERO_FLOW_TEST_ARM === 'C')));
  useEffect(() => {
    if (heroFlowVariant && heroFlowCommunityId) logHeroFlowExposure(heroFlowCommunityId, heroFlowVariant);
  }, [heroFlowVariant, heroFlowCommunityId]);

  // "Watch the video" (community.aboutVideo). With aboutVideoABTest on, only
  // variant B gets the button over the hero, C gets the video's still under
  // the title and tagline (its own first screen, below), A gets neither — for visitors who aren't logged
  // in; logged as the 'community_about_video' test. ?video=1 / 2 / 0 force
  // B / C / A to check (not logged).
  const aboutVideoTesting = !!sanitizedCommunity?.aboutVideo && sanitizedCommunity?.aboutVideoABTest === true && !user;
  const aboutVideoTest = useMemo(
    () => (aboutVideoTesting && heroFlowCommunityId ? getAboutVideoVariant(heroFlowCommunityId) : null),
    [aboutVideoTesting, heroFlowCommunityId]
  );
  const showAboutVideo = !!sanitizedCommunity?.aboutVideo && (!aboutVideoTesting || aboutVideoTest?.variant === 'B');
  const showInlineVideo = !!sanitizedCommunity?.aboutVideo && aboutVideoTesting && aboutVideoTest?.variant === 'C';
  useEffect(() => {
    if (aboutVideoTest && !aboutVideoTest.forced && heroFlowCommunityId) logAboutVideoExposure(heroFlowCommunityId, aboutVideoTest.variant);
  }, [aboutVideoTest, heroFlowCommunityId]);

  // Log the A/B exposure once we know which community the visitor landed on
  useEffect(() => {
    const communityId = sanitizedCommunity?.communityId || community?._id;
    if (communityId) {
      if (!demoButtonForced()) logDemoButtonExposure(communityId, showDemoButton ? 'A' : 'B');
    }
  }, [sanitizedCommunity?.communityId, community?._id, showDemoButton]);

  // Remove the static hero shell from the pre-built HTML in the same frame
  // the real page renders (layout effect = before paint), so the swap from
  // shell to app is invisible.
  useLayoutEffect(() => {
    if (sanitizedCommunity || isNotFound) document.getElementById("static-shell")?.remove();
  }, [sanitizedCommunity, isNotFound]);

  // Where Join goes for visitors who aren't logged in. With joinChatABTest
  // on: the sign-up form (A), or the chat sign-up — /meet/<ref>, questions
  // first, then the account (B); logged as 'community_join_route' only when
  // they tap Join — that's when they meet their route. ?join=chat /
  // ?join=form force one to check (not logged).
  const joinRouteTesting = sanitizedCommunity?.joinChatABTest === true && !user && !!sanitizedCommunity?.databaseRef;
  const joinRoute = useMemo(
    () => (joinRouteTesting && heroFlowCommunityId ? getJoinRouteVariant(heroFlowCommunityId) : null),
    [joinRouteTesting, heroFlowCommunityId]
  );
  const onJoinClick = () => {
    if (joinRoute && !joinRoute.forced && heroFlowCommunityId) logJoinRouteExposure(heroFlowCommunityId, joinRoute.variant);
  };
  const joinHref = joinRoute?.variant === 'B'
    ? `/meet/${sanitizedCommunity.databaseRef}`
    : `/groupsearch/join/${sanitizedCommunity?.communityId || community?._id}`;

  // Visit analytics for the admin "Page Analytics" tab
  useCommunityPageAnalytics(sanitizedCommunity?.communityId || community?._id, {
    communityTitle: sanitizedCommunity?.title,
    communityRef: sanitizedCommunity?.databaseRef,
    heroFlow: sanitizedCommunity?.heroFlow === 'quickmatch' ? (showQuickMatch ? (quickMatchIntro ? 'choice' : 'quickmatch') : 'default') : undefined,
    heroFlowVariant: heroFlowVariant || undefined,
    aboutVideo: sanitizedCommunity?.aboutVideo ? (showAboutVideo || showInlineVideo ? 'shown' : 'hidden') : undefined,
    aboutVideoVariant: aboutVideoTest?.variant || undefined,
    isLoggedIn: !!user?.userId,
    isMember: !!(user?.listOfCommunities && community?.communityId && user.listOfCommunities.includes(community.communityId)),
  });

  // "X people have been looking to connect" social-proof badge — local-only
  // experiment for now, not yet wired into the deployed site's copy.
  useEffect(() => {
    const communityId = sanitizedCommunity?.communityId || community?._id;
    if (!communityId) return;
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    if (!apiBase) return;
    fetch(`${apiBase}/ab-test/interest/${communityId}`)
      .then(res => res.json())
      .then(data => setInterestBadge(data))
      .catch(() => {});
  }, [sanitizedCommunity?.communityId, community?._id]);

  // Analyze background image brightness when community data loads
  useEffect(() => {
    if (sanitizedCommunity?.pics) {
      const backgroundImageUrl = sanitizedCommunity.pics.split(',')[0]?.trim() || PLACEHOLDER_IMG;
      analyzeImageBrightness(backgroundImageUrl);
    }
  }, [sanitizedCommunity?.pics]);

  // v2 layout: hide nav links, show only Interconnector wordmark
  useEffect(() => {
    if (sanitizedCommunity?.homePageLayout === 'v2') {
      document.body.classList.add('community-v2-layout');
    }
    return () => document.body.classList.remove('community-v2-layout');
  }, [sanitizedCommunity?.homePageLayout]);

  // v4 layout: add body class + data attributes so Navbar can read the community title and ID
  useEffect(() => {
    if (sanitizedCommunity?.homePageLayout !== 'v4') return;
    document.body.classList.add('community-v4-layout');
    document.body.dataset.v4Title = sanitizedCommunity.title || '';
    document.body.dataset.v4CommunityId = sanitizedCommunity.communityId || community?._id || '';
    return () => {
      document.body.classList.remove('community-v4-layout');
      delete document.body.dataset.v4Title;
      delete document.body.dataset.v4CommunityId;
    };
  }, [sanitizedCommunity?.homePageLayout, sanitizedCommunity?.title, sanitizedCommunity?.communityId, community?._id]);

  // Preload first carousel image so it loads as high priority (above the fold)
  useEffect(() => {
    if (!sanitizedCommunity?.pics) return;
    const pics = Array.isArray(sanitizedCommunity.pics)
      ? sanitizedCommunity.pics
      : sanitizedCommunity.pics.split(',').map(p => p.trim()).filter(Boolean);
    // With a hero video, its first-frame still is the hero image instead.
    const first = (sanitizedCommunity.heroVideo && !sanitizedCommunity.carousel && sanitizedCommunity.heroVideoPoster) || pics[0];
    if (!first) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    // With a srcset, give no href: browsers that can't read imagesrcset then
    // skip the preload instead of fetching a wrong-sized copy as well.
    const srcset = heroSrcSet(first);
    if (srcset) {
      if (!('imageSrcset' in link)) return;
      link.imageSrcset = srcset; link.imageSizes = '100vw';
    } else {
      link.href = first;
    }
    link.fetchPriority = 'high';
    document.head.appendChild(link);
    return () => { if (link.parentNode) link.parentNode.removeChild(link); };
  }, [sanitizedCommunity?.pics, sanitizedCommunity?.heroVideo, sanitizedCommunity?.heroVideoPoster, sanitizedCommunity?.carousel]);

  // Hero carousel — cycle through pics when carousel: true
  useEffect(() => {
    if (!sanitizedCommunity?.carousel) return;
    const heroPics = sanitizedCommunity.pics.split(',').map(img => img.trim()).filter(img => img);
    if (heroPics.length <= 1) return;
    const interval = setInterval(() => {
      setHeroCarouselIndex(prev => (prev + 1) % heroPics.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [sanitizedCommunity?.carousel, sanitizedCommunity?.pics]);

  // Re-analyse brightness on hero carousel image change
  useEffect(() => {
    if (!sanitizedCommunity?.carousel) return;
    const heroPics = sanitizedCommunity.pics.split(',').map(img => img.trim()).filter(img => img);
    const url = heroPics[heroCarouselIndex] || heroPics[0];
    if (url) analyzeImageBrightness(url);
  }, [heroCarouselIndex, sanitizedCommunity?.carousel]);

  // Ordered pic array for imageStrips backgrounds — indexed sequentially across sections
  const stripPics = useMemo(() => {
    if (!sanitizedCommunity?.imageStrips || !sanitizedCommunity?.pics) return [];
    return sanitizedCommunity.pics.split(',').map(img => img.trim()).filter(img => img);
  }, [sanitizedCommunity?.imageStrips, sanitizedCommunity?.pics]);

  // Image slider for Get Introduced section
  useEffect(() => {
    if (!sanitizedCommunity?.pics) return;
    
    const images = sanitizedCommunity.pics.split(',').map(img => img.trim()).filter(img => img);
    if (images.length <= 1) return; // No need for slider if only one image
    
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % images.length);
    }, 5000); // 5 seconds

    return () => clearInterval(interval);
  }, [sanitizedCommunity?.pics]);

  // Determine community theme class based on type or specific community
  const getCommunityThemeClass = () => {
    if (!sanitizedCommunity) return '';
    
    // Special community themes
    if (sanitizedCommunity.title?.toLowerCase().includes('edinburgh fringer') || 
        sanitizedCommunity.title?.toLowerCase().includes('fringe')) {
      return 'community-theme-edinburgh-fringer';
    }
    
    // Theme based on community type
    const type = sanitizedCommunity.type?.toLowerCase() || '';
    
    if (type.includes('sport') || type.includes('fitness') || type.includes('gym')) {
      return 'community-theme-sports';
    }
    if (type.includes('extreme')) {
      return 'community-theme-extreme-sports';
    }
    if (type.includes('professional') || type.includes('business') || type.includes('networking')) {
      return 'community-theme-professional';
    }
    if (type.includes('tech') || type.includes('startup')) {
      return 'community-theme-technology';
    }
    if (type.includes('creative') || type.includes('art') || type.includes('design')) {
      return 'community-theme-creative';
    }
    if (type.includes('music') || type.includes('band')) {
      return 'community-theme-music';
    }
    if (type.includes('social') || type.includes('community')) {
      return 'community-theme-social';
    }
    if (type.includes('food') || type.includes('cooking')) {
      return 'community-theme-food';
    }
    if (type.includes('travel') || type.includes('adventure')) {
      return 'community-theme-travel';
    }
    if (type.includes('literature') || type.includes('book') || type.includes('reading')) {
      return 'community-theme-literature';
    }
    if (type.includes('education') || type.includes('learning')) {
      return 'community-theme-education';
    }
    if (type.includes('support') || type.includes('wellness') || type.includes('health')) {
      return 'community-theme-support';
    }
    
    // Default theme
    return 'community-theme-general';
  };

  // Check if user is already a member
  const isUserMember = () => {
    return user?.listOfCommunities?.includes(community?.communityId);
  };

  // Handle connecting to community members (for existing members)
  const handleConnectToMembers = () => {
    if (community?.communityId) {
      navigate(`/messenger?communityId=${community.communityId}&action=connect`);
    }
  };

  const handleJoin = async () => {
    if (!community) return;
    if (user?.userId) {
      if (isUserMember()) {
        // Instead of alert, redirect to connect with members
        handleConnectToMembers();
        return;
      }
      try {
        const auth = getAuth();
        const token = await auth.currentUser.getIdToken(true);
        const requestBody = {
          communityId: community.communityId,
          userId: user.userId,
        };
        const requestOptions = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify(requestBody),
        };
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/communiUser/joinCommunity`,
          requestOptions
        );
        if (response.ok) {
          const data = await response.json();
          dispatch({
            type: "UPDATE_COMMUNITIES",
            payload: data.profile.listOfCommunities,
          });
          // Already a member (their group list here was out of date): no
          // second sign-up — straight to their chat with the group's bot.
          if (data.alreadyMember) {
            handleConnectToMembers();
            return;
          }

          // Trigger profile refresh to ensure all components get fresh data
          console.log('🔄 [Community Home] Triggering profile refresh after joining community');
          setTimeout(() => {
            if (refreshUserProfile) {
              refreshUserProfile();
            }
          }, 100);

          navigate("/joinAdditionalCommunity/" + community.communityId);
        } else {
          console.error("Failed to join community");
        }
      } catch (error) {
        console.error("Error joining community:", error);
      }
    } else {
      onJoinClick();
      navigate(joinHref);
    }
  };

  // The site menu's Join does the same as this page's (utils/navJoin.js) —
  // shown only to visitors who aren't members of this community.
  const handleJoinRef = useRef(handleJoin);
  handleJoinRef.current = handleJoin;
  const showNavJoin = !!community && !isUserMember();
  useEffect(() => {
    if (!showNavJoin) return undefined;
    setNavJoin(() => handleJoinRef.current());
    return () => setNavJoin(null);
  }, [showNavJoin]);

  const sanitizeUrl = (url) => {
    const sanitizedUrl = DOMPurify.sanitize(url);
    const a = document.createElement("a");
    a.href = sanitizedUrl;
    return a.href;
  };

  // Hero-shaped skeleton (not a spinner) so the page already looks like itself
  // while the data loads — only seen on communities without a pre-built page.
  if (isLoading) return (
    <div className="home-content-container_communityHome">
      <section className="section_communityHome hero-section_communityHome community-skeleton_communityHome" style={{ position: 'relative' }} aria-busy="true" aria-label="Loading community">
        <div className="section-content_communityHome" style={{ position: 'relative', zIndex: 2 }}>
          <div className="community-skeleton-bar_communityHome" style={{ width: 'min(70%, 420px)', height: '2.6rem' }} />
          <div className="community-skeleton-bar_communityHome" style={{ width: 'min(85%, 520px)', height: '1.2rem', marginTop: '1.5rem' }} />
          <div className="community-skeleton-bar_communityHome" style={{ width: 'min(60%, 380px)', height: '1.2rem', marginTop: '0.6rem' }} />
          <div className="community-skeleton-bar_communityHome" style={{ width: '140px', height: '3rem', marginTop: '2rem', borderRadius: '999px' }} />
        </div>
      </section>
    </div>
  );
  if (isNotFound)
    return (
      <div className="page-not-found">
        <h2>Page Not Found</h2>
        <p>To infinite and beyond!</p>
        <Link to="/">Return to Homepage</Link>
      </div>
    );

  return sanitizedCommunity ? (
  <div className={`home-content-container_communityHome ${getCommunityThemeClass()}${tookOverFromShell ? ' from-static-shell_communityHome' : ''}`}>
    <Helmet>
      <title>{sanitizedCommunity.title} - The Interconnector</title>
      <meta name="description" content={sanitizedCommunity.topLevelSummary} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://theinterconnector.com/${sanitizedCommunity.databaseRef}`} />
      <meta property="og:title" content={sanitizedCommunity.title} />
      <meta property="og:description" content={sanitizedCommunity.topLevelSummary} />
      <meta property="og:image" content={sanitizedCommunity.pics?.split(',')[0]?.trim() || PLACEHOLDER_IMG} />
      <meta property="og:site_name" content="The Interconnector" />
      
      {/* Twitter Card */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={`https://theinterconnector.com/${sanitizedCommunity.databaseRef}`} />
      <meta property="twitter:title" content={sanitizedCommunity.title} />
      <meta property="twitter:description" content={sanitizedCommunity.topLevelSummary} />
      <meta property="twitter:image" content={sanitizedCommunity.pics?.split(',')[0]?.trim() || PLACEHOLDER_IMG} />
    </Helmet>
  {/* SECTION: V4 Hero — full-screen carousel, Join + Try now only */}
{sanitizedCommunity.homePageLayout === 'v4' && (() => {
  const heroPics = Array.isArray(sanitizedCommunity.pics)
    ? sanitizedCommunity.pics
    : sanitizedCommunity.pics.split(',').map(img => img.trim()).filter(img => img);
  return (
    <section className="section_communityHome v2-hero-section_communityHome imagestrip-fullscreen">
      {heroPics.map((pic, i) => (
        <LazyBackground
          key={i}
          className={`hero-carousel-image_communityHome ${i === heroCarouselIndex ? 'active' : ''}`}
          url={i === 0 ? null : sanitizeUrl(pic)}
          enabled={isNearSlide(i, heroCarouselIndex, heroPics.length)}
        >
          {i === 0 && (
            <img
              src={sizedImageUrl(sanitizeUrl(pic), 1600)}
              srcSet={heroSrcSet(sanitizeUrl(pic))}
              sizes="100vw"
              alt=""
              fetchPriority="high"
              loading="eager"
              style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )}
        </LazyBackground>
      ))}
      <div className="v2-hero-overlay_communityHome" />
      <div className="v4-hero-content_communityHome">
        <h2 className="v2-hero-heading_communityHome">Get introduced</h2>
        {user ? (
          isUserMember() ? (
            <button className="connect-button_communityHome" onClick={handleConnectToMembers}>
              Connect with Members
            </button>
          ) : (
            <div className="v4-hero-cta_communityHome">
              <div className="non-member-buttons_communityHome">
                <button className="join-button_communityHome" onClick={handleJoin}>Join</button>
                {shouldShowDemoButton && (
                  <Link
                    to={`/meet/${sanitizedCommunity.databaseRef}?mode=demo`}
                    className="try-now-button_communityHome"
                  >
                    Demo
                  </Link>
                )}
              </div>
              <p className="v4-hero-free_communityHome">Free. No costs. Zilch.</p>
            </div>
          )
        ) : (
          <div className="v4-hero-cta_communityHome">
            <div className="non-member-buttons_communityHome">
              <Link
                to={joinHref}
                onClick={onJoinClick}
                className="join-button_communityHome"
              >
                Join
              </Link>
              {shouldShowDemoButton && (
                <Link
                  to={`/meet/${sanitizedCommunity.databaseRef}?mode=demo`}
                  className="try-now-button_communityHome"
                >
                  Demo
                </Link>
              )}
            </div>
            <p className="v4-hero-free_communityHome">Free. No costs. Zilch.</p>
          </div>
        )}
      </div>
      {heroPics.length > 1 && (
        <div className="carousel-nav_communityHome">
          {heroPics.map((_, i) => (
            <button
              key={i}
              className={`carousel-dot_communityHome${i === heroCarouselIndex ? ' active' : ''}`}
              onClick={() => setHeroCarouselIndex(i)}
              aria-label={`Image ${i + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
})()}

  {/* SECTION: V2 Hero — carousel first strip with minimal content */}
{sanitizedCommunity.homePageLayout === 'v2' && (() => {
  const heroPics = sanitizedCommunity.pics.split(',').map(img => img.trim()).filter(img => img);
  return (
    <section className="section_communityHome v2-hero-section_communityHome imagestrip-fullscreen">
      {heroPics.map((pic, i) => (
        <LazyBackground
          key={i}
          className={`hero-carousel-image_communityHome ${i === heroCarouselIndex ? 'active' : ''}`}
          url={i === 0 ? null : sanitizeUrl(pic)}
          enabled={isNearSlide(i, heroCarouselIndex, heroPics.length)}
        >
          {i === 0 && (
            <img
              src={sizedImageUrl(sanitizeUrl(pic), 1600)}
              srcSet={heroSrcSet(sanitizeUrl(pic))}
              sizes="100vw"
              alt=""
              fetchPriority="high"
              loading="eager"
              style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )}
        </LazyBackground>
      ))}
      <div className="v2-hero-overlay_communityHome" />
      <div className="v2-hero-content_communityHome">
        <h2 className="v2-hero-heading_communityHome">Get introduced</h2>
        <p className="v2-hero-community-name_communityHome">
          ({sanitizedCommunity.title}...)
        </p>
        <div className="community-hero-join_communityHome">
          {user ? (
            isUserMember() ? (
              <div className="member-section_communityHome">
                <p className="member-status_communityHome">✅ You are a member of this community</p>
                <button className="connect-button_communityHome" onClick={handleConnectToMembers}>
                  Connect with Members
                </button>
              </div>
            ) : (
              <button className="join-button_communityHome" onClick={handleJoin}>Join</button>
            )
          ) : (
            <div className="non-member-buttons_communityHome">
              <Link
                to={joinHref}
                onClick={onJoinClick}
                className="join-button_communityHome"
              >
                Join
              </Link>
              {shouldShowDemoButton && (
                <Link
                  to={`/meet/${sanitizedCommunity.databaseRef}?mode=demo`}
                  className="try-now-button_communityHome"
                >
                  Demo
                </Link>
              )}
            </div>
          )}
        </div>
        <p className="v2-hero-free-text_communityHome">
          Free to join. Free to use. No costs.
        </p>
      </div>
      {heroPics.length > 1 && (
        <div className="carousel-nav_communityHome">
          {heroPics.map((_, i) => (
            <button
              key={i}
              className={`carousel-dot_communityHome${i === heroCarouselIndex ? ' active' : ''}`}
              onClick={() => setHeroCarouselIndex(i)}
              aria-label={`Image ${i + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
})()}

  {/* SECTION: Community Hero (above the fold) — v1 only */}
{sanitizedCommunity.homePageLayout !== 'v2' && sanitizedCommunity.homePageLayout !== 'v4' && (() => {
  const heroPics = sanitizedCommunity.pics.split(',').map(img => img.trim()).filter(img => img);
  // With a hero video, its first-frame still replaces the first photo.
  const heroStill = (sanitizedCommunity.heroVideo && !sanitizedCommunity.carousel && sanitizedCommunity.heroVideoPoster) || heroPics[0] || PLACEHOLDER_IMG;
  // Video test, variant C: over the hero photo, the title, the tagline, the
  // video's still in a hexagon (tapping it opens the player), Join and "Free
  // to join". No Demo
  // button. Only for visitors who aren't logged in (the test's audience),
  // so Join is always the non-member one.
  if (showInlineVideo && !showQuickMatch) return (
<section className="section_communityHome videohero_communityHome">
  {/* The hero photo behind everything, as in A and B. */}
  <img
    src={sizedImageUrl(sanitizeUrl(heroStill), 1600)}
    srcSet={heroSrcSet(sanitizeUrl(heroStill))}
    sizes="100vw"
    alt=""
    fetchPriority="high"
    loading="eager"
    className="videohero-bg_communityHome"
  />
  <div className="videohero-body_communityHome">
    <h1 className="community-title-block_communityHome videohero-title_communityHome">{sanitizedCommunity.title}</h1>
    <h3 className="community-hero-subtitle_communityHome videohero-tagline_communityHome">
      {sanitizedCommunity.heroTagline || 'Find, Connect and Match'}
    </h3>
    <AboutVideoInline
      src={sanitizedCommunity.aboutVideo}
      poster={sanitizedCommunity.aboutVideoPoster}
      title={sanitizedCommunity.title}
      label={sanitizedCommunity.aboutVideoLabel}
      className="videohero-video_communityHome"
    />
    {/* Join in the same wrappers as A and B, so it's laid out the same. */}
    <div className="community-hero-join_communityHome">
      <div className="non-member-buttons_communityHome">
        <Link to={joinHref} onClick={onJoinClick} className="join-button_communityHome">Join</Link>
        {/* Demo, as in A and B (its own A/B test; ?demo=1 shows it). */}
        {shouldShowDemoButton && (
          <Link to={`/meet/${sanitizedCommunity.databaseRef}?mode=demo`} className="try-now-button_communityHome">Demo</Link>
        )}
      </div>
    </div>
    <p className="videohero-free_communityHome">Free to join. Free to use. No costs!</p>
  </div>
  <button
    type="button"
    className="scroll-cue_communityHome videohero-scroll_communityHome"
    aria-label="Find out more"
    onClick={(e) => e.currentTarget.closest('section')?.nextElementSibling?.scrollIntoView({ behavior: 'smooth' })}
  >
    <svg className="scroll-cue-chevron_communityHome" width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 9l7 7 7-7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </button>
</section>
  );
  return (
<section
  className="section_communityHome hero-section_communityHome"
  style={{ position: 'relative' }}
>
  {/* Real <img> (not a CSS background-image) so the browser's preload
      scanner can discover and fetch it as early as possible. */}
  <img
    src={sizedImageUrl(sanitizeUrl(heroStill), 1600)}
    srcSet={heroSrcSet(sanitizeUrl(heroStill))}
    sizes="100vw"
    alt=""
    fetchPriority="high"
    loading="eager"
    style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
  />
  {sanitizedCommunity.heroVideo && !sanitizedCommunity.carousel && (
    <HeroVideo src={sanitizedCommunity.heroVideo} mobileSrc={sanitizedCommunity.heroVideoMobile} />
  )}
  {sanitizedCommunity.carousel && heroPics.map((pic, index) => (
    index === 0 ? null : (
      <LazyBackground
        key={index}
        className={`hero-carousel-image_communityHome ${index === heroCarouselIndex ? 'active' : ''}`}
        url={sanitizeUrl(pic)}
        enabled={isNearSlide(index, heroCarouselIndex, heroPics.length)}
      />
    )
  ))}
  {needsDarkOverlay === true && (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      zIndex: 1
    }}></div>
  )}
  {needsTopGradient && (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '50%',
      background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.8) 15%, rgba(0, 0, 0, 0.6) 25%, rgba(0, 0, 0, 0.3) 40%, transparent 100%)',
      zIndex: 2
    }}></div>
  )}
  <div className={`section-content_communityHome${sanitizedCommunity.interconnectingMode === 'countdown' && COUNTDOWN_STRIP_VERSION === 'vc2' ? ' vc2-hero-content_communityHome' : ''}${showQuickMatch ? ' quickmatch-hero-content_communityHome' : ''}`} style={{ position: 'relative', zIndex: 2 }}>
    <h1 className="community-title-block_communityHome">{sanitizedCommunity.title}</h1>
    {/* "Watch the video" (community.aboutVideo): below the tagline, just
        above Join / Demo — or here, under the title, when the quick-match
        card replaces them. */}
    {showAboutVideo && showQuickMatch && (
      <AboutVideo src={sanitizedCommunity.aboutVideo} poster={sanitizedCommunity.aboutVideoPoster} title={sanitizedCommunity.title} label={sanitizedCommunity.aboutVideoLabel} />
    )}
    {sanitizedCommunity.interconnectingMode === 'countdown' && COUNTDOWN_STRIP_VERSION === 'vc2' && sanitizedCommunity.suggestionsRounds && (
      // Invisible placeholder — same element/classes the real tagline
      // uses, so its own height + margins hold exactly the same gap
      // between title and the actions box that the tagline used to
      // occupy, without hardcoding a guessed margin value.
      <h3 className="community-hero-subtitle_communityHome" style={{ fontSize: '1.5rem', visibility: 'hidden' }} aria-hidden="true">
        placeholder
      </h3>
    )}
    <div className="community-hero-actions_communityHome">
      {!showQuickMatch && sanitizedCommunity.heroStyle === 'story' && sanitizedCommunity.interconnectingMode !== 'countdown' && (
        <HeroStory text={sanitizedCommunity.heroStory} effect={sanitizedCommunity.heroStoryEffect} />
      )}
      {/* The tagline (community.heroTagline): over the normal hero and over
          the quick-match card (B and C) alike. */}
      {!(sanitizedCommunity.interconnectingMode === 'countdown' && COUNTDOWN_STRIP_VERSION === 'vc2') && !(sanitizedCommunity.heroStyle === 'story' && sanitizedCommunity.interconnectingMode !== 'countdown') && (
        <h3 className={`community-hero-subtitle_communityHome${showQuickMatch ? ` qm-tagline${quickMatchTaglineOut || (quickMatchIntro && !C_OPENS_ON_CHOICE) ? ' qm-tagline--out' : ''}` : ''}`} style={{
          color: 'white',
          textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
          fontSize: '1.5rem'
        }}>
          {(sanitizedCommunity.interconnectingMode === 'countdown' && sanitizedCommunity.heroTaglineCountdown)
            || sanitizedCommunity.heroTagline
            || 'Find, Connect and Match'}
        </h3>
      )}
      {showQuickMatch && (
        <QuickMatchHero
          communityId={sanitizedCommunity.communityId || community?._id}
          fields={sanitizedCommunity.quickMatchFields}
          peopleWord={sanitizedCommunity.quickMatchPeopleWord}
          retentionMonths={sanitizedCommunity.quickMatchRetentionMonths}
          country={sanitizedCommunity.quickMatchCountry}
          floating={sanitizedCommunity.quickMatchStyle === 'floating'}
          intro={quickMatchIntro}
          onIntroLeave={() => setQuickMatchTaglineOut(true)}
          matchLabel={sanitizedCommunity.matchLabel}
        />
      )}
      {sanitizedCommunity.interconnectingMode === 'countdown' && COUNTDOWN_STRIP_VERSION !== 'vc2' && sanitizedCommunity.suggestionsRounds && (
        <div style={{ marginTop: '1.5rem' }}>
          <WeeklyMatchCountdown
            dayOfWeek={sanitizedCommunity.suggestionsRounds.dayOfWeek}
            hour={sanitizedCommunity.suggestionsRounds.hour}
            label={sanitizedCommunity.countdownLabel}
          />
        </div>
      )}
      {sanitizedCommunity.interconnectingMode === 'countdown' && COUNTDOWN_STRIP_VERSION === 'vc2' && sanitizedCommunity.suggestionsRounds && (
        <div style={{ marginTop: 'clamp(2.5rem, 10vw, 5rem)', marginBottom: '1.5rem' }}>
          <WeeklyMatchCountdown
            dayOfWeek={sanitizedCommunity.suggestionsRounds.dayOfWeek}
            hour={sanitizedCommunity.suggestionsRounds.hour}
            label={sanitizedCommunity.countdownLabel}
          />
        </div>
      )}
      {showAboutVideo && !showQuickMatch && (
        <AboutVideo src={sanitizedCommunity.aboutVideo} poster={sanitizedCommunity.aboutVideoPoster} title={sanitizedCommunity.title} label={sanitizedCommunity.aboutVideoLabel} />
      )}
      {!showQuickMatch && <div className="community-hero-join_communityHome">
        {user ? (
          isUserMember() ? (
            <div className="member-section_communityHome">
              <p className="member-status_communityHome">
                ✅ You are a member of this community
              </p>
              <button 
                className="connect-button_communityHome" 
                onClick={handleConnectToMembers}
              >
                Connect with Members
              </button>
            </div>
          ) : (
            <button className="join-button_communityHome" onClick={handleJoin}>
              Join
            </button>
          )
        ) : (
          <div className="non-member-buttons_communityHome">
            <Link
              to={joinHref}
              onClick={onJoinClick}
              className="join-button_communityHome"
            >
              Join
            </Link>
            {shouldShowDemoButton && (
              <Link
                to={`/meet/${sanitizedCommunity.databaseRef}?mode=demo`}
                className="try-now-button_communityHome"
              >
                Demo
              </Link>
            )}
          </div>
        )}
      </div>}
      {!showQuickMatch && <p style={{ display: 'inline', background: '#fff', color: '#000', padding: '0.1em 0.35em', lineHeight: 1.55, boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone', fontSize: 'clamp(1.15rem, 2.6vw, 1.4rem)', fontWeight: 700, letterSpacing: '0.01em' }}>
        Free to join. Free to use. No costs!
      </p>}
    </div>
    {/* Every section is a full screen tall, so nothing on the first
        screen hints there's more below — most visitors never scrolled. On phones
        this sits on the bottom edge of the first screen (see CSS). */}
    <button
      type="button"
      className="scroll-cue_communityHome"
      aria-label="Find out more"
      onClick={(e) => e.currentTarget.closest('section')?.nextElementSibling?.scrollIntoView({ behavior: 'smooth' })}
    >
      <svg className="scroll-cue-chevron_communityHome" width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M5 9l7 7 7-7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  </div>
</section>
  );
})()}

{/* COUNTDOWN MODE — vc2 only: a second strip right below the hero,
    plainly explaining what the weekly round actually means. vc1 is the
    hero-only treatment with no further explanation strip.
    LOCAL EXPERIMENT ONLY, not yet pushed live. */}
{sanitizedCommunity.interconnectingMode === 'countdown' && COUNTDOWN_STRIP_VERSION === 'vc2' && (() => {
  const bgPic = stripPics[1] || null;
  return (
    <section
      className={`section_communityHome${bgPic ? ' imagestrip-fullscreen' : ''}`}
      style={{ background: 'transparent', overflow: 'hidden', position: 'relative' }}
    >
      {bgPic && (
        <>
          <LazyBackground url={sanitizeUrl(bgPic)} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />
          <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1 }} />
        </>
      )}
      <div className="section-content_communityHome" style={{ position: 'relative', zIndex: 2, textAlign: 'center', maxWidth: '640px' }}>
        <h2 className="section-heading_communityHome" style={{ marginBottom: '1rem', color: '#fff', textShadow: '0 2px 10px rgba(0,0,0,0.6)' }}>
          How the weekly round works
        </h2>
        <p style={{ color: '#e0e0e0', fontSize: 'clamp(1rem, 2vw, 1.2rem)', lineHeight: 1.6, textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
          Each week, we'll suggest up to 5 people who we think you might want to connect with to explore London's cultural scene with, based on what you tell us. We use smart matching techniques to find people we truly think you'll get on with. If we can't find anyone, we'll relook next week!
        </p>
      </div>
    </section>
  );
})()}

{/* INTEREST STRIP — V1: tiered community/sitewide/quiet count, matching
    the "Get introduced" imagestrip-fullscreen pattern, across all states:
    - 'community': real per-community count (big number + quality caption)
    - 'sitewide': community itself is low-traffic, falls back to the
      site-wide count instead — framed as "on the Interconnector", never
      implying it's this specific group
    - 'quiet': both are too low to show a credible number; quality-over-
      quantity message instead (currently unreachable given the sitewide
      fallback, kept for when that tier is ever removed again)
    LOCAL EXPERIMENT ONLY, not yet pushed live.
    Toggle INTEREST_STRIP_VERSION below to compare against V2. */}
{INTEREST_STRIP_VERSION === 'v1' && interestBadge?.show && (() => {
  const bgPic = stripPics[1] || null;
  const { scope, count } = interestBadge;
  return (
    <section
      className={`section_communityHome${bgPic ? ' imagestrip-fullscreen' : ''}`}
      style={{ background: 'transparent', overflow: 'hidden', position: 'relative' }}
    >
      {bgPic && (
        <>
          <LazyBackground url={sanitizeUrl(bgPic)} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />
          <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1 }} />
        </>
      )}
      <div className="section-content_communityHome" style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
        {scope === 'quiet' && (
          <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 1rem', boxSizing: 'border-box' }}>
            <div style={{ color: '#e0e0e0', fontSize: 'clamp(1.3rem, 4vw, 1.8rem)', fontWeight: 700, lineHeight: 1.4, textShadow: '0 2px 10px rgba(0,0,0,0.6)' }}>
              {getQuietMessage(sanitizedCommunity.matchLabel)}
            </div>
          </div>
        )}
        {(scope === 'community' || scope === 'sitewide') && (
          <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 1rem', boxSizing: 'border-box' }}>
            <div style={{ color: '#e8e8e8', fontSize: 'clamp(3rem, 12vw, 5rem)', fontWeight: 800, lineHeight: 1, textShadow: '0 2px 10px rgba(0,0,0,0.6)' }}>
              <AnimatedCount value={count} /> people
            </div>
            <div style={{ color: '#cfcfcf', fontSize: 'clamp(1.15rem, 2vw, 1.55rem)', fontWeight: 600, marginTop: '0.75rem', textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
              {scope === 'community'
                ? <>actively interested in connecting in {sanitizedCommunity.title} in the last 24 hours</>
                : <>actively interested in connecting on the Interconnector in the last 24 hours</>}
            </div>
            <div style={{ color: '#d3d3d3', fontSize: 'clamp(0.95rem, 1.6vw, 1.2rem)', fontWeight: 400, lineHeight: 1.5, marginTop: 'clamp(3rem, 9vw, 5rem)', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
              {getQuietMessage(sanitizedCommunity.matchLabel)}
            </div>
          </div>
        )}
      </div>
    </section>
  );
})()}

{/* INTEREST STRIP — V2: flat site-wide activity total, no tiers/scopes —
    always "X people active on the Interconnector in the last 24 hours"
    using the raw sitewideCount, regardless of this community's own traffic.
    LOCAL EXPERIMENT ONLY, not yet pushed live. */}
{INTEREST_STRIP_VERSION === 'v2' && interestBadge?.sitewideCount != null && !(sanitizedCommunity.interconnectingMode === 'countdown' && COUNTDOWN_STRIP_VERSION === 'vc2') && (() => {
  const bgPic = stripPics[1] || null;
  const { sitewideCount } = interestBadge;
  // Under 10 in the last 24h reads as thin rather than reassuring — fall
  // back to a fixed, safely-conservative "20+ have joined" framing instead
  // of the small real number.
  const showRealCount = sitewideCount >= 10;
  return (
    <section
      className={`section_communityHome${bgPic ? ' imagestrip-fullscreen' : ''}`}
      style={{ background: 'transparent', overflow: 'hidden', position: 'relative' }}
    >
      {bgPic && (
        <>
          <LazyBackground url={sanitizeUrl(bgPic)} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />
          <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1 }} />
        </>
      )}
      <div className="section-content_communityHome" style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 1rem', boxSizing: 'border-box' }}>
          <div style={{ color: '#e8e8e8', fontSize: 'clamp(3rem, 12vw, 5rem)', fontWeight: 800, lineHeight: 1, textShadow: '0 2px 10px rgba(0,0,0,0.6)' }}>
            {showRealCount ? <AnimatedCount value={sitewideCount} /> : '20+'} people
          </div>
          <div style={{ color: '#cfcfcf', fontSize: 'clamp(1.15rem, 2vw, 1.55rem)', fontWeight: 600, marginTop: '0.75rem', textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
            {showRealCount ? 'active in the last 24 hours' : 'have joined the Interconnector'}
          </div>
          {!(sanitizedCommunity.interconnectingMode === 'countdown' && COUNTDOWN_STRIP_VERSION === 'vc2') && (
            <div style={{ color: '#d3d3d3', fontSize: 'clamp(0.95rem, 1.6vw, 1.2rem)', fontWeight: 400, lineHeight: 1.5, marginTop: 'clamp(3rem, 9vw, 5rem)', textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>
              {getQuietMessage(
                sanitizedCommunity.matchLabel,
                showRealCount ? undefined : "We are a small but growing community. Though, it's not about numbers..."
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
})()}

{/* SECTION: Testimonials — second strip, community-specific or default */}
{(() => {
  const displayTestimonials = sanitizedCommunity.testimonials?.length > 0
    ? sanitizedCommunity.testimonials
    : DEFAULT_TESTIMONIALS;
  const testimonialBg = stripPics[2] || null;
  return (
    <section
      className={`section_communityHome${testimonialBg ? ' imagestrip-fullscreen' : ''}`}
      style={{
        background: testimonialBg ? 'transparent' : 'var(--bg-light, #f9f9f9)',
        padding: '3rem 1.5rem',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {testimonialBg && (
        <>
          <LazyBackground url={sanitizeUrl(testimonialBg)} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />
          <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1 }} />
        </>
      )}
      <div className="section-content_communityHome" style={{ position: 'relative', zIndex: 2 }}>
        <h2 className="section-heading_communityHome" style={testimonialBg ? { color: 'white' } : {}}>What our members say</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', justifyContent: 'center', marginTop: '1.5rem' }}>
          {displayTestimonials.map((t, i) => (
            <div key={i} style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem 1.75rem',
              maxWidth: '380px',
              flex: '1 1 280px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              position: 'relative'
            }}>
              <span style={{ fontSize: '2rem', lineHeight: 1, color: 'var(--primary, #1976d2)', display: 'block', marginBottom: '0.75rem' }}>"</span>
              <p style={{ margin: 0, fontStyle: 'italic', color: '#333', lineHeight: 1.6 }}>{t.text}</p>
              {t.author && (
                <p style={{ margin: '1rem 0 0', fontWeight: 600, fontSize: '0.9rem', color: '#666' }}>— {t.author}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
})()}

{/* SECTION: Steps Explainer - Second Section */}
<section
  className={`section_communityHome steps-section2_communityHome${stripPics[3] ? ' imagestrip-fullscreen' : ''}`}
  style={stripPics[3] ? { background: 'transparent', overflow: 'hidden' } : {}}
>
  {stripPics[3] && (
    <>
      <LazyBackground url={sanitizeUrl(stripPics[3])} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />
      <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1 }} />
    </>
  )}
  <div className="section-content_communityHome" style={stripPics[3] ? { position: 'relative', zIndex: 2 } : {}}>
    <StepsExplainer_CommunityHomeSection2
      step1="Describe who you want to connect with"
      step2={sanitizedCommunity.interconnectingMode === 'countdown' ? 'Get weekly suggestions of compatible people' : 'Get suggested compatible people'}
      step3="Match and connect!"
    />
  </div>
</section>


  {/* SECTION: Description */}
  {(() => {
    // Photo behind the details cards — the 10th community photo (the 9th is
    // the photo strip just below), else the second-to-last.
    const allPics = (sanitizedCommunity.pics || '').split(',').map(img => img.trim()).filter(Boolean);
    const detailsBg = allPics[9] || allPics[Math.max(0, allPics.length - 2)] || null;
    return (
  <section
    className={`section_communityHome details-section_communityHome${detailsBg ? ' imagestrip-fullscreen' : ''}`}
    style={detailsBg ? { background: 'transparent', overflow: 'hidden' } : {}}
  >
    {detailsBg && (
      <>
        <LazyBackground url={sanitizeUrl(detailsBg)} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />
        <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1 }} />
      </>
    )}
    <div className="section-content_communityHome" style={detailsBg ? { position: 'relative', zIndex: 2 } : {}}>
      <div className="detail-cards_communityHome">
        <div className="detail-card_communityHome">
          <div className="detail-header_communityHome">
            <span className="detail-icon_communityHome">📍</span>
            <strong>Location</strong>
          </div>
          <p>{sanitizedCommunity.location}</p>
        </div>
        <div className="detail-card_communityHome">
          <div className="detail-header_communityHome">
            <span className="detail-icon_communityHome">🔖</span>
            <strong>Type</strong>
          </div>
          <p>{sanitizedCommunity.type}</p>
        </div>
        <div className="detail-card_communityHome">
          <div className="detail-header_communityHome">
            <span className="detail-icon_communityHome">🎯</span>
            <strong>Purpose</strong>
          </div>
          <p>{sanitizedCommunity.topLevelSummary}</p>
        </div>
        <div className="detail-card_communityHome">
          <div className="detail-header_communityHome">
            <span className="detail-icon_communityHome">🛂</span>
            <strong>Eligibility</strong>
          </div>
          <p>{sanitizedCommunity.eligibilityDescription || "Open to all community members"}</p>
        </div>
        <div className="detail-card_communityHome">
          <div className="detail-header_communityHome">
            <span className="detail-icon_communityHome">💸</span>
            <strong>Cost</strong>
          </div>
          <p>{sanitizedCommunity.costStructure?.description || "Contact for pricing details"}</p>
        </div>
        <div className="detail-card_communityHome">
          <div className="detail-header_communityHome">
            <span className="detail-icon_communityHome">👥</span>
            <strong>Gender</strong>
          </div>
          <p>Choose to connect with people of the same gender as you, or with any gender in the group.</p>
        </div>
        {sanitizedCommunity.matchingOptions && (
          <div className="detail-card_communityHome">
            <div className="detail-header_communityHome">
              <span className="detail-icon_communityHome">🤝</span>
              <strong>Matching Options</strong>
            </div>
            <p>{sanitizedCommunity.matchingOptions}</p>
          </div>
        )}
      </div>
    </div>
  </section>
    );
  })()}

  {/* Photo strip right after the details cards (moved up to where Get
      Introduced was) — a community photo not used by the strips above. */}
  {(() => {
    const allPics = (sanitizedCommunity.pics || '').split(',').map(img => img.trim()).filter(Boolean);
    const pic = allPics[8] || allPics[allPics.length - 1];
    return pic ? (
      <section className="section_communityHome photo-strip_communityHome">
        <LazyBackground url={sanitizeUrl(pic)} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      </section>
    ) : null;
  })()}

{/* SECTION: People Might Connect */}
{(() => {
  const isV2 = sanitizedCommunity.homePageLayout === 'v2';
  const bgPic = isV2 ? (stripPics[0] || null) : (sanitizedCommunity.imageStrips ? (stripPics[4] || null) : null);
  const useV2Style = isV2 && bgPic;
  const reasons = sanitizedCommunity.connectionReasons?.length > 0
    ? sanitizedCommunity.connectionReasons
    : ['Connect', 'Learn', 'Share', 'Grow'];
  return (
    <section
      className={`section_communityHome people-connect-section_communityHome${bgPic ? ' imagestrip-fullscreen' : ''}${useV2Style ? ' v2-people-connect_communityHome' : ''}`}
      style={bgPic ? { background: 'transparent', overflow: 'hidden', position: 'relative' } : {}}
    >
      {bgPic && (
        <>
          <LazyBackground url={sanitizeUrl(bgPic)} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />
          <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1 }} />
        </>
      )}
      <div className="section-content_communityHome" style={bgPic ? { position: 'relative', zIndex: 2, ...(useV2Style ? { width: '100%', maxWidth: '100%', padding: 0 } : {}) } : {}}>
        <h2
          className="section-heading_communityHome"
          style={bgPic ? { color: 'white', ...(useV2Style ? { padding: '0 var(--space-md)', marginBottom: 'var(--space-md)' } : {}) } : {}}
        >
          Find other people to
        </h2>
        <div className={`connect-reasons-grid_communityHome${useV2Style ? ' connect-reasons-grid-v2_communityHome' : ''}`}>
          {reasons.map((reason, index) => (
            <div key={index} className={`connect-reason-card_communityHome${useV2Style ? ' connect-reason-card-v2_communityHome' : ''}`}
              style={bgPic && !useV2Style ? { fontSize: '1.05em' } : {}}>
              <span className={`connect-reason-text_communityHome${useV2Style ? ' connect-reason-text-v2_communityHome' : ''}`}
                style={bgPic && !useV2Style ? { fontSize: '1.05em' } : {}}>
                {reason}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
})()}

{/* SECTION: A matching app for... */}
<section
  className={`section_communityHome edinburgh-description-section_communityHome${stripPics[5] ? ' imagestrip-fullscreen' : ''}`}
  style={stripPics[5] ? { background: 'transparent', overflow: 'hidden' } : {}}
>
  {stripPics[5] && (
    <>
      <LazyBackground url={sanitizeUrl(stripPics[5])} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />
      <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1 }} />
    </>
  )}
  <div className="section-content_communityHome" style={stripPics[5] ? { position: 'relative', zIndex: 2 } : {}}>
    <h3 className="edinburgh-description-text_communityHome">
      A people finding app for {taglineEnding(sanitizedCommunity.homePageTagline) || 'anyone looking to connect and build meaningful relationships'}.
    </h3>
  </div>
</section>

{/* SECTION: Community Bot Demo */}
{/* 
<section className="section_communityHome demo-bot-section_communityHome">
  <div className="section-content_communityHome">
    <h2 className="section-heading_communityHome">Chat to your well connected Community Bot!</h2>
    <DemoCommunityBot 
      communityName={sanitizedCommunity?.title || "Edinburgh Fringe"} 
      communityId={community?._id || "674e72fd088bfc5b32ad3842"}
    />
  </div>
</section>
*/}

{/* SECTION: Sponsor/Partner */}
{(sanitizedCommunity.sponsor || sanitizedCommunity.externalOrganisation) && (
  <section className="section_communityHome partner-section_communityHome">
    <div className="section-content_communityHome">
      <div className="partner-container_communityHome">
        <div className="partner-badge_communityHome">
          <div className="partner-label_communityHome">
            {sanitizedCommunity.sponsor ? "Sponsored by" : "In partnership with"}
          </div>
          <div className="partner-content_communityHome">
            <div className="partner-image-container_communityHome">
              <img
                src={
                  sanitizedCommunity.sponsor 
                    ? sanitizeUrl(sanitizedCommunity.sponsorImage)
                    : sanitizeUrl(sanitizedCommunity.externalOrganisationImage)
                }
                alt={
                  sanitizedCommunity.sponsor 
                    ? sanitizedCommunity.sponsorName
                    : sanitizedCommunity.externalOrganisationName
                }
                className="partner-image_communityHome"
              />
            </div>
            <div className="partner-name_communityHome">
              {sanitizedCommunity.sponsor 
                ? sanitizedCommunity.sponsorName
                : sanitizedCommunity.externalOrganisationName}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
)}





 


  {/* SECTION: Get Introduced */}
  {sanitizedCommunity.homePageLayout !== 'v4' && (() => {
    const images = sanitizedCommunity.pics
      ? sanitizedCommunity.pics.split(',').map(img => img.trim()).filter(img => img)
      : [];
    const isCarousel = sanitizedCommunity.imageStrips && images.length > 0;
    const reverseIndex = images.length > 0 ? (images.length - 1 - currentImageIndex) : 0;
    return (
      <section
        className={`section_communityHome intro-section_communityHome${isCarousel ? ' imagestrip-fullscreen' : ''}`}
        style={isCarousel ? { background: 'transparent', overflow: 'hidden' } : {}}
      >
        {/* Full-bleed carousel background when imageStrips */}
        {isCarousel && images.map((pic, index) => (
          <LazyBackground
            key={index}
            className={`hero-carousel-image_communityHome ${index === reverseIndex ? 'active' : ''}`}
            url={sanitizeUrl(pic)}
            enabled={isNearSlide(index, reverseIndex, images.length)}
          />
        ))}
        {isCarousel && (
          <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1 }} />
        )}

        {/* Heading */}
        <div style={isCarousel ? { position: 'relative', zIndex: 2, textAlign: 'center' } : {}}>
          {isCarousel
            ? <h2 className="section-heading_communityHome">Get introduced</h2>
            : (
              <div className="section-content_communityHome">
                <h2 className="section-heading_communityHome">Get introduced</h2>
                {images.length > 0 && (() => {
                  const currentImage = images[reverseIndex] || images[0] || PLACEHOLDER_IMG;
                  return (
                    <div className="image-slider-container_communityHome">
                      <div className="image-container_communityHome">
                        <img
                          src={sizedImageUrl(sanitizeUrl(currentImage), 1200)}
                          loading="lazy"
                          alt={`${sanitizedCommunity.title} community`}
                          className="slider-image_communityHome"
                          onLoad={e => {
                            const img = e.currentTarget;
                            const aspectRatio = img.naturalWidth / img.naturalHeight;
                            if (aspectRatio > 1.3) {
                              img.style.objectPosition = 'center top';
                              img.setAttribute('data-aspect', 'landscape');
                            } else if (aspectRatio < 0.8) {
                              img.style.objectPosition = 'center center';
                              img.setAttribute('data-aspect', 'portrait');
                            } else {
                              img.style.objectPosition = 'center center';
                              img.setAttribute('data-aspect', 'square');
                            }
                          }}
                          onError={e => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = PLACEHOLDER_IMG;
                          }}
                        />
                      </div>
                      {images.length > 1 && (
                        <div className="slider-dots_communityHome">
                          {images.map((_, index) => (
                            <div
                              key={index}
                              className={`slider-dot_communityHome ${index === reverseIndex ? 'active' : ''}`}
                              onClick={() => setCurrentImageIndex(images.length - 1 - index)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )
          }
        </div>

        {/* Carousel navigation — dots only, pinned to bottom */}
        {isCarousel && images.length > 1 && (
          <div className="carousel-nav_communityHome">
            {images.map((_, i) => (
              <button
                key={i}
                className={`carousel-dot_communityHome${i === reverseIndex ? ' active' : ''}`}
                onClick={() => setCurrentImageIndex(images.length - 1 - i)}
                aria-label={`Image ${i + 1}`}
              />
            ))}
          </div>
        )}
      </section>
    );
  })()}

  {/* SECTION: Main Description */}
  <section
    className={`section_communityHome description-section_communityHome${stripPics[6] ? ' imagestrip-fullscreen' : ''}`}
    style={stripPics[6] ? { background: 'transparent', position: 'relative', overflow: 'hidden' } : {}}
  >
    {stripPics[6] && (
      <>
        <LazyBackground url={sanitizeUrl(stripPics[6])} style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundSize: 'cover', backgroundPosition: 'center', zIndex: 0 }} />
        <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1 }} />
      </>
    )}
    <div className="section-content_communityHome" style={stripPics[6] ? { position: 'relative', zIndex: 2 } : {}}>
      <h2 className="section-heading_communityHome" style={stripPics[6] ? { color: 'white' } : {}}>Our Community</h2>
      <div className="community-description_communityHome">
        <p dangerouslySetInnerHTML={{ __html: sanitizedCommunity.mainDescription }}></p>
      </div>
    </div>
  </section>

  {/* SECTION: CTA */}
  {(() => {
    const ctaBgPic = stripPics[7] || null;
    return (
      <section
        className={`section_communityHome cta-section_communityHome${ctaBgPic ? ' imagestrip-fullscreen' : ''}`}
        style={ctaBgPic ? { background: 'transparent' } : {}}
      >
        {sanitizedCommunity.imageStrips && ctaBgPic && (
          <>
            <LazyBackground url={sanitizeUrl(ctaBgPic)} style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              zIndex: 0
            }} />
            <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1 }} />
          </>
        )}
        <div
          className="section-content_communityHome"
          style={ctaBgPic ? { position: 'relative', zIndex: 2 } : {}}
        >
          <p style={{ color: 'white', fontSize: 'clamp(1.6rem, 4vw, 2.4rem)', fontWeight: 700, marginBottom: '2rem', lineHeight: 1.3 }}>
            Free to join. Free to use. No costs!
          </p>
          <div className="community-hero-join_communityHome" style={{ marginBottom: '2rem' }}>
            {user ? (
              isUserMember() ? (
                <div className="member-section_communityHome">
                  <p className="member-status_communityHome">✅ You are already a member</p>
                  <button className="connect-button_communityHome" onClick={handleConnectToMembers}>
                    Connect with Members
                  </button>
                </div>
              ) : (
                <button className="join-button_communityHome" onClick={handleJoin}>
                  Join
                </button>
              )
            ) : (
              <div className="non-member-buttons_communityHome">
                <Link
                  to={joinHref}
                  onClick={onJoinClick}
                  className="join-button_communityHome"
                >
                  Join
                </Link>
                {shouldShowDemoButton && (
                  <Link
                    to={`/meet/${sanitizedCommunity.databaseRef}?mode=demo`}
                    className="try-now-button_communityHome"
                  >
                    Demo
                  </Link>
                )}
              </div>
            )}
          </div>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '1rem', letterSpacing: '0.03em' }}>
            Start matching with compatible members today
          </p>
        </div>
      </section>
    );
  })()}

  {/* SECTION: Tags — large hashtags filling the strip */}
  {(() => {
    const tagList = sanitizedCommunity.tags
      ? sanitizedCommunity.tags.split(',').map(t => t.trim()).filter(Boolean)
      : [];
    if (!tagList.length) return null;
    return (
      <section className="tags-hashtag-section_communityHome">
        <div className="tags-hashtag-inner_communityHome">
          {tagList.map((tag, i) => (
            <span key={i} className="tags-hashtag-item_communityHome">
              #{tag}
            </span>
          ))}
        </div>
      </section>
    );
  })()}
</div>

  ) : null;
};

export default CommunityHome;
